import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { alpha } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import ListIcon from '@mui/icons-material/List';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { get as _get } from 'lodash';

import Auth from '../../../data/auth-service';
import AlertsService from '../../../data/alerts-service';
import MetadataService from '../../../data/metadata-service';

import Alerts from '../alerts/main';
import Metadata from '../metadata/main';

const useStyles = makeStyles(theme => ({
    root: {
        position: 'relative',
    },
    // decided not to use here - a little homemade looking?
    // keeping style just incase
    primaryToggle: {
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        paddingTop: 5,
        paddingBottom: 5,
        '&.Mui-selected': {
            color: theme.palette.getContrastText(theme.palette.primary.main),
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
            },
        },
    },
}));

const IncidentData = props => {
    const classes = useStyles();

    const [type, setType] = useState('alerts');
    const [sources, setSources] = useState(['conn']);
    const [incidentsMetadataService, setIncidentsMetadataService] = useState();
    const [incidentsAlertsService, setIncidentsAlertsService] = useState();

    const handleShowData = (e, v) => setType(v);

    const getAlerts = useCallback(() => {
        if (!props.incident) return Promise.resolve([]);
        return Promise.resolve({
            data: props.incident.alerts || [],
            asof: new Date(),
        });
    }, [props.incident]);

    const getMetadata = useCallback(() => {
        if (!props.incident) return Promise.resolve([]);
        return Promise.resolve({
            data: (props.incident.metadata || []).map(d => d.data),
            asof: new Date(),
            took: 0,
            total: (props.incident.metadata || []).length,
        });
    }, [props.incident]);

    // initial hook
    useEffect(() => {
        setIncidentsAlertsService({
            ...AlertsService,
            getData: getAlerts,
            getTimeline: () => Promise.resolve({ objects: [] }),
            GetLazyTimeline: () => Promise.resolve(),
            cacheSavedFilters: () => Promise.resolve([]),
        });
        (async () => {
            await MetadataService.cacheFields(true);
            setIncidentsMetadataService({
                ...MetadataService,
                getGroups: () => Promise.resolve({}),
                getTimeline: () => Promise.resolve({ objects: [] }),
                GetLazyTimeline: () => Promise.resolve(),
                getData: getMetadata
            });
        })();

        setSources((_get(props.incident, 'metadata', []) || [])
            .map(d => _get(d, 'data._source.file_name', '')));
    }, [props.incident, getAlerts, getMetadata]);

    return (
        <Box className={classes.root}>
            <Grid
                style={{ marginLeft: 10, marginTop: 10, marginBottom: 0 }}
                container
                alignItems="center"
                justifyContent="space-between"
                direction="row"
                wrap="nowrap">
                <Grid item style={{ paddingBottom: 0 }}>
                    <ToggleButtonGroup
                        size="small"
                        value={type}
                        exclusive
                        onChange={handleShowData}
                        aria-label="show data">
                        <ToggleButton
                            onChange={handleShowData}
                            value="metadata"
                            aria-label="metadata"
                            fontSize="small">
                            <span fontSize="small"> Metadata </span>
                        </ToggleButton>
                        <ToggleButton
                            onChange={handleShowData}
                            value="alerts"
                            aria-label="alerts"
                            fontSize="small">
                            <span fontSize="small"> Alerts </span>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Grid>
            </Grid>
            <Grid
                container
                direction="column"
                justifyContent="flex-start"
                alignItems="flex-start">
                <Grid item style={{ width: 'calc(100% - 10px)' }}>
                    {{
                        alerts: incidentsAlertsService && <Alerts
                            perm={Auth.perm}
                            hideTimelinebar={true}
                            hideFilterbar={true}
                            modes={[
                                { key: 'list', icon: <ListIcon/>, title: 'list alerts' },
                                { key: 'detail', icon: <VisibilityIcon/>, title: 'view details' },
                            ]}
                            service={incidentsAlertsService}
                            headerSize={props.collapsed ? 355 : 425}
                            miniToolbar={true}
                            live={false}
                            sortingMode='client'
                            profile='incidentalerts'
                        />,
                        metadata: incidentsMetadataService && <Metadata
                            perm={Auth.perm}
                            hideTimelinebar={true}
                            hideFilterbar={true}
                            modes={[
                                { key: 'list', icon: <ListIcon/>, title: 'list metadata' },
                                { key: 'detail', icon: <VisibilityIcon/>, title: 'view details' },
                            ]}
                            service={incidentsMetadataService}
                            headerSize={props.collapsed ? 355 : 425}
                            miniToolbar={true}
                            live={false}
                            sortingMode='client'
                            profile='incidentmetadata'
                            sources={sources}/>,
                    }[type]}
                </Grid>
            </Grid>
        </Box>
    );
};
IncidentData.propTypes = {
    incident: PropTypes.object,
    collapsed: PropTypes.bool,
};

export default IncidentData;
